import React, { useState } from 'react';
import axios from 'axios';

import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import ConfigurableOption from '~/components/core/Molecules/ConfigurableOption';
import Heading from '~/components/core/TextComponents/Heading';
import Text from '~/components/core/TextComponents/Text';
import { useCms } from '~/components/hooks/useCms';
import { useOrganizationDetails } from '~/components/hooks/useOrganizationDetails';
import useDataFetcher from '~/components/useDataFetcher';
import type { MetabaseConfigurationResponse } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import CardDialog from '../../../CardDialog';

interface NotInitializedViewProps {
  isSuperOrg: boolean;
  isInitializing: boolean;
  onInitialize: () => void;
}

const NotInitializedView: React.FC<NotInitializedViewProps> = ({ isSuperOrg, isInitializing, onInitialize }) => (
  <div className="flex flex-col gap-16 p-24">
    <div>
      <Heading variant={Heading.TYPES.H2}>Metabase Configuration</Heading>
      <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.PRIMARY} className="mt-8 opacity-70">
        Metabase has not been initialized for this organization.
      </Text>
    </div>
    {isSuperOrg && (
      <div>
        <Button color="primary" onClick={onInitialize} disabled={isInitializing}>
          {isInitializing ? 'Initializing...' : 'Initialize Metabase'}
        </Button>
        <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.PRIMARY} className="mt-8 opacity-50">
          This will create a Metabase group and collection structure for this organization.
        </Text>
      </div>
    )}
  </div>
);

interface InitializedViewProps {
  isSuperOrg: boolean;
  isEnabled: boolean;
  isUpdating: boolean;
  initializedAt: string | null;
  onToggleEnabled: (newValue: boolean) => void;
}

const InitializedView: React.FC<InitializedViewProps> = ({
  isSuperOrg,
  isEnabled,
  isUpdating,
  initializedAt,
  onToggleEnabled,
}) => (
  <div className="flex flex-col gap-16">
    {isSuperOrg ? (
      <ConfigurableOption
        isEnabled={isEnabled}
        onChangeToggle={onToggleEnabled}
        configurationView={
          <>
            <Heading variant={Heading.TYPES.H3}>Enable Metabase</Heading>
            <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.PRIMARY} className="opacity-50">
              Allow users in this organization to access Metabase dashboards
            </Text>
          </>
        }
        showOnly={isUpdating}
      />
    ) : (
      <div className="p-24">
        <Heading variant={Heading.TYPES.H3}>Metabase Status</Heading>
        <AlertBanner
          className="mt-16"
          alertType={isEnabled ? AlertBanner.ALERT_TYPES.DONE : AlertBanner.ALERT_TYPES.INFO}
          title={isEnabled ? 'Metabase is enabled' : 'Metabase is disabled'}
          note={
            isEnabled
              ? 'Your organization has access to Metabase dashboards.'
              : 'Contact your administrator to enable Metabase access.'
          }
        />
      </div>
    )}

    {initializedAt && (
      <div className="px-24 pb-24">
        <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.PRIMARY} className="opacity-50">
          Initialized: {new Date(initializedAt).toLocaleDateString()}
        </Text>
      </div>
    )}
  </div>
);

const MetabaseConfigurationTab: React.FC = () => {
  const { organization } = useOrganizationDetails({ kind: 'sysconfig' });
  const { userOrganization: nonImpersonatedOrganization } = useCms();

  const [isUpdating, setIsUpdating] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  const url = `/sysconfig/api/v1/configuration/organizations/${organization?.id}/configuration`;
  const { data, isLoading, isError, reloadData } = useDataFetcher<MetabaseConfigurationResponse>(url, null, {
    shouldFetch: Boolean(organization?.id),
  });

  const isSuperOrg = Boolean(nonImpersonatedOrganization?.is_super_organization);

  const handleInitialize = async () => {
    if (!organization?.id) return;

    try {
      setIsInitializing(true);
      await axios.post(`/sysconfig/api/v1/configuration/organizations/${organization.id}/initialize`);
      await reloadData();
    } catch (error) {
      reportAxiosError(error);
    } finally {
      setIsInitializing(false);
    }
  };

  const handleToggleEnabled = async (newValue: boolean) => {
    if (!organization?.id) return;

    try {
      setIsUpdating(true);
      await axios.patch(`/sysconfig/api/v1/configuration/organizations/${organization.id}/update`, {
        is_enabled: newValue,
      });
      await reloadData();
    } catch (error) {
      reportAxiosError(error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="bg-slate-100 pt-32">
      <div className="mx-auto max-w-screen-md px-16">
        <CardDialog width="md" noCardTitle>
          <LoadingSwitch isError={isError} isLoading={isLoading}>
            {data?.is_initialized ? (
              <InitializedView
                isSuperOrg={isSuperOrg}
                isEnabled={Boolean(data?.is_enabled)}
                isUpdating={isUpdating}
                initializedAt={data?.initialized_at ?? null}
                onToggleEnabled={handleToggleEnabled}
              />
            ) : (
              <NotInitializedView
                isSuperOrg={isSuperOrg}
                isInitializing={isInitializing}
                onInitialize={handleInitialize}
              />
            )}
          </LoadingSwitch>
        </CardDialog>
      </div>
    </div>
  );
};

export default MetabaseConfigurationTab;
